/**
 * Tracks the last chat line the local player actually sent, so the "say last"
 * hotkey (default **Ctrl+Q**, the `say-last` keybinding action) can recall it
 * into the chat input for a one-key resend or a quick edit before resending.
 *
 * Pure and DOM-free so it unit-tests in node like `input/keybindings.ts`:
 * `main.ts` owns one {@link LastSaid} and `record`s each *outgoing* line on the
 * say path (after the client-only `/dress` intercept, so only lines that reach
 * the room count as "sent"), and the say-last handler `recall`s it and hands it
 * to `chat.ts` for the actual input write. It is a **recall into the input**,
 * never an auto-send: the player still presses Enter (or edits first).
 */
export class LastSaid {
  /** The most recent line the player sent, or `null` until they have sent one. */
  private line: string | null = null;

  /**
   * Remember a line the player just sent. A blank (whitespace-only) line is
   * ignored - there is nothing meaningful to recall - so a stray Enter never
   * wipes out an earlier, real recall target.
   */
  record(text: string): void {
    if (text.trim().length > 0) this.line = text;
  }

  /**
   * The last sent line to recall into the input, or `null` when the player has
   * not sent anything yet (an empty-history recall is a no-op for the caller).
   */
  recall(): string | null {
    return this.line;
  }
}
